import Modal, { IModalProps } from "@/component/Modal";
import { useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom/client";
import SearchIcon from "@/assets/summary/search.svg?react";
import RoleSelect from "../RoleSelect";
import { EditableSharePermissionList, RoleType } from "@/constants/share";
import DeleteIcon from "@/assets/common/ic_item_delete.svg?react";
import StaffItem from "./StaffItem";
import { inviteShare } from "@/services/share";
import StaffList from "./StaffList";
import useGetStaffList from "@/sections/Share/useGetStaffList";

interface ShareInfo {
  shareId: string;
  mySharePermission: RoleType;
  onCloseCb: () => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  handleError: (error: any) => void;
}
export interface IStaffAddModal
  extends ShareInfo,
  Pick<IModalProps, "visible" | "onClose"> { }

const StaffAddModal: React.FC<IStaffAddModal> = ({
  visible,
  mySharePermission,
  shareId,
  onClose,
  handleError,
}) => {
  const {
    keyword,
    staffList,
    subscribedList,
    showLoadMore,
    isLoading,
    loadError,
    setKeyword,
    setStaffList,
    setSubscribedList,
    onListScroll,
    retry,
  } = useGetStaffList(shareId);

  const [inputFocus, setInputFocus] = useState(false);
  const [sharePermission, setSharePermission] = useState(RoleType.Viewer);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const onBlur = () => {
    setInputFocus(false);
  };
  const onFocus = () => {
    setInputFocus(true);
  };

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const handleChoose = (identityId: string, value: boolean) => {
    if (value) {
      const targetStaffIndex = staffList.findIndex(
        (item) => item.identityId === identityId
      );
      staffList[targetStaffIndex].subscribed = value;
      setStaffList([...staffList]);
      // 选中
      if (!subscribedList.find((item) => item.identityId === identityId)) {
        subscribedList.push(staffList[targetStaffIndex]);
        setSubscribedList([...subscribedList]);
      }
      return;
    }
    // 取消选中
    handleDelete(identityId);
  };

  const handleDelete = (identityId: string) => {
    // 修改左边列表
    const targetStaffIndex = staffList.findIndex(
      (item) => item.identityId === identityId
    );
    if (targetStaffIndex !== -1) {
      staffList[targetStaffIndex].subscribed = false;
      setStaffList([...staffList]);
    }
    // 修改右边列表
    const newSubscribedStaffList = subscribedList.filter(
      (item) => item.identityId !== identityId
    );
    setSubscribedList([...newSubscribedStaffList]);
  };

  const handleSave = async () => {
    try {
      await inviteShare({
        shareId,
        identitySharePermissions: subscribedList.map((item) => {
          return {
            identityId: item.identityId,
            sharePermission,
          };
        }),
      });
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      handleError(error);
    }
    onClose();
  };

  return (
    <Modal visible={visible} onClose={onClose} maskClosable>
      <div className="flex w-148 h-130 bg-white rounded-2.5 shadow relative overflow-x-hidden overflow-y-hidden">
        <div className="flex flex-col py-4 min-w-88 b-1px b-r-solid b-#F4F6F9 pb-18">
          <div
            onClick={() => {
              setInputFocus(true);
              inputRef.current?.focus();
            }}
            onBlur={onBlur}
            className={`
              min-h-8 rounded-2.5 
              flex items-center font-400
              b-1 b-solid  mx-6 
              ${inputFocus ? " b-mx-primary" : "b-#ECEEF3"}
            `}
          >
            <SearchIcon className="size-4 mx-2" />
            <input
              ref={inputRef}
              onChange={(e) => {
                setKeyword(e.target.value);
              }}
              onFocus={onFocus}
              placeholder="输名字搜索通讯录成员"
              onBlur={onBlur}
              value={keyword}
              autoFocus
              className="w-full b-0 outline-none text-3 text-mx-secondary w-31.5 mr-2 placeholder:text-#CED2D9"
            />
          </div>
          <div className="mt-4"></div>
          {!keyword && (
            <div className="px-6 min-h-10 lh-10 text-#30343C font-500 text-3.5">
              最近
            </div>
          )}
          <StaffList
            staffList={staffList}
            handleChoose={handleChoose}
            showLoadMore={showLoadMore}
            onListScroll={onListScroll}
            isLoading={isLoading}
            loadError={loadError}
            retry={retry}
            keyword={keyword}
          />
        </div>
        <div className="relative flex-1 flex flex-col pb-18 px-2">
          <div className="px-2 h-14 flex items-center justify-between text-3 text-mx-tertiary">
            <div>{`已选：${subscribedList.length}个`}</div>
            <div className="w-15">
              <RoleSelect
                disabled={
                  !EditableSharePermissionList.includes(mySharePermission)
                }
                roleType={sharePermission}
                setRoleType={(roleType) =>
                  setSharePermission(roleType as RoleType)
                }
                showList={[RoleType.Viewer, RoleType.Manager]}
              />
            </div>
          </div>
          <div className="flex-1 text-3.5 text-#30343C overflow-auto my-scrollbar">
            {subscribedList.map((staff, index) => {
              return (
                <div
                  key={staff.identityId}
                  className="px-2 flex items-center w-full h-12 justify-between hover:bg-#F4F6F9 rd-2"
                >
                  <StaffItem staff={staff} index={index} />
                  <DeleteIcon
                    className="cursor-pointer"
                    onClick={() => {
                      handleDelete(staff.identityId);
                    }}
                  />
                </div>
              );
            })}
          </div>
          <div className="absolute right-4 flex items-center bottom-4">
            <div
              className="w-22 h-8 text-3 bg-#ECEEF3 rd-2.5 text-#30343C flex items-center justify-center cursor-pointer"
              onClick={onClose}
            >
              取消
            </div>
            <div
              className={`ml-4 w-22 h-8 text-3 bg-#30343C rd-2.5 flex items-center justify-center ${subscribedList.length === 0
                ? "text-#fff/30 cursor-default"
                : "text-#fff cursor-pointer"
                }`}
              onClick={handleSave}
            >
              邀请
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export const openStaffAddModal = (info: ShareInfo) => {
  const root = document.getElementById("root");
  const div = document.createElement("div");
  root?.appendChild(div);
  const close = () => {
    root?.removeChild(div);
    info.onCloseCb && info.onCloseCb();
  };
  const modal = <StaffAddModal visible onClose={close} {...info} />;
  ReactDOM.createRoot(div).render(modal);
};

export default StaffAddModal;
